import { format, isSameMonth } from 'date-fns';
import type { PeriodSelection } from '@/lib/period-presets';
import type { SpaceDetail } from '@/types/spaces';
import type { RegisterSpaceSettlement, SpaceLedger, SpaceTransfer } from '@/types/space-settlements';

export function settlementPeriod(kind: SpaceDetail['kind'], value: string | null, now = new Date()): string {
  if (kind === 'trip') return 'trip';
  return value && value !== '9999-12' && /^(?!0000)\d{4}-(0[1-9]|1[0-2])$/.test(value) ? value : now.toISOString().slice(0, 7);
}

// Despesas follows the app's period picker (the same one the personal pages use), but a space has one period per
// month (or the whole trip). A selection inside one month IS that month (the picker clamps the current month to today,
// and a monthly read does not care which days were picked); one spanning months narrows to the month it ends in, which
// is the month "A vencer" reads. `span` is what the LISTING follows instead: every whole month the selection touches
// (`yyyy-MM..yyyy-MM`), so a "últimos 6 meses" lists six months of expenses rather than hiding all but the last one.
export function spacePeriodFromSelection(kind: SpaceDetail['kind'], selection: PeriodSelection): { period: string; span: string } {
  if (kind === 'trip') return { period: 'trip', span: 'trip' };
  const period = format(selection.to, 'yyyy-MM');
  if (isSameMonth(selection.from, selection.to)) return { period, span: period };
  return { period, span: `${format(selection.from, 'yyyy-MM')}..${period}` };
}

/**
 * The ledger Acerto reads: the space's RUNNING balance — everything since it began minus every settlement ever
 * registered, so a debt left in a past month is still owed (ruled 2026-09-23). The API answers a trip with its own
 * `trip` key. Months stay for reading activity (Despesas, the home's budget and fixed expenses).
 */
export const RUNNING_LEDGER = 'all';

export function settlementPeriodLabel(period: string): string {
  if (period === 'trip') return 'Toda a viagem';
  if (period === RUNNING_LEDGER) return 'Saldo em aberto';
  return new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${period}-01T00:00:00Z`));
}

export function transferKey(transfer: SpaceTransfer): string {
  return JSON.stringify([transfer.fromUserId, transfer.toUserId]);
}

export function transferHint(transfer: SpaceTransfer, userId?: string | null): string {
  if (transfer.fromUserId === userId) return 'você paga';
  if (transfer.toUserId === userId) return 'você recebe';
  return 'entre outras pessoas';
}

export function settlementRequest(ledger: SpaceLedger, selected: string[], requestKey: string): RegisterSpaceSettlement {
  return { periodKey: ledger.periodKey, ledgerVersion: ledger.ledgerVersion, requestKey,
    transfers: ledger.suggestedTransfers.filter((transfer) => selected.includes(transferKey(transfer))).map((transfer) => ({ ...transfer })) };
}

export const DEPARTED_MEMBER = 'Ex-integrante';

export function participantName(space: SpaceDetail, userId: string): string {
  const member = space.members.find((person) => person.userId === userId);
  // Someone who left and whose name the API no longer has. Ruled 2026-09-20: just "Ex-integrante", never the raw
  // account id (it means nothing to people and is not theirs to read). Two such people read alike; rows stay apart
  // by their ids in keys, and the API names a departed person wherever it still can (payer, responsible, actor).
  if (!member) return DEPARTED_MEMBER;
  const label = memberLabel(member);
  // Names are optional and not unique; this screen tells people whom to reimburse.
  const ambiguous = space.members.some((person) => person.userId !== userId && memberLabel(person).toLocaleLowerCase('pt-BR') === label.toLocaleLowerCase('pt-BR'));
  return ambiguous ? `${label} · ${member.email?.trim() || userId}` : label;
}

function memberLabel(member: SpaceDetail['members'][number]): string {
  return member.name?.trim() || 'Integrante';
}

// The photo a member agreed to show the space; nothing for a departed person or someone who kept the initials.
// Keyed by userId on purpose: participantName may append an e-mail to tell namesakes apart.
export function memberPhoto(space: SpaceDetail, userId: string): string | null {
  return space.members.find((person) => person.userId === userId)?.photoUrl ?? null;
}

export function settlementBalanceDisplay(cents: number): { label: string; color: string } {
  if (cents > 0) return { label: 'Ainda recebe', color: 'text-success-text' };
  if (cents < 0) return { label: 'Ainda paga', color: 'text-error-text' };
  return { label: 'Saldo quitado', color: 'text-text-primary' };
}

export function settlementPlanNotice(space: SpaceDetail): string {
  if (space.archivedAt) return 'Espaço encerrado. O histórico continua disponível.';
  if (space.myRole === 'viewer') return 'Você tem acesso somente de leitura. Um editor pode registrar o acerto.';
  return 'Registre apenas as transferências já realizadas. Uma pessoa pode aparecer em mais de uma transferência.';
}
